import { groupLayerMarkers } from '../modules/layer-screen-clusters';
import { drillDownTransactionCluster } from '../modules/transaction-drilldown';
import GeoJsonFetchLayer from './GeoJsonFetchLayer';
import { MarkerGroup, createHtmlMarker } from '../utils/markers';
import { addOrUpdateGeoJsonSource, removeSourceAndLayers } from '../utils/maplibre-helpers';

var EARTH_RADIUS_METERS = 6371000;

function buildRadiusPolygon(lng, lat, radiusMeters, pointCount) {
    var originLat = lat * Math.PI / 180;
    var originLng = lng * Math.PI / 180;
    var angularDistance = radiusMeters / EARTH_RADIUS_METERS;
    var coordinates = [];
    var totalPoints = Math.max(24, pointCount || 64);

    for (var index = 0; index <= totalPoints; index += 1) {
        var bearing = (index / totalPoints) * Math.PI * 2;
        var nextLat = Math.asin(
            Math.sin(originLat) * Math.cos(angularDistance)
            + Math.cos(originLat) * Math.sin(angularDistance) * Math.cos(bearing)
        );
        var nextLng = originLng + Math.atan2(
            Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(originLat),
            Math.cos(angularDistance) - Math.sin(originLat) * Math.sin(nextLat)
        );

        coordinates.push([
            ((nextLng * 180 / Math.PI + 540) % 360) - 180,
            nextLat * 180 / Math.PI,
        ]);
    }

    return {
        type: 'FeatureCollection',
        features: [{
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [coordinates],
            },
            properties: {},
        }],
    };
}

function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (char) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
}

function bboxParamsFromString(bbox) {
    var parts = String(bbox || '').split(',').map(function (value) {
        return parseFloat(value);
    });
    if (parts.length !== 4 || parts.some(function (value) { return !isFinite(value); })) {
        return {};
    }
    return {
        min_lng: parts[0],
        min_lat: parts[1],
        max_lng: parts[2],
        max_lat: parts[3],
    };
}

function queryString(params) {
    var search = new URLSearchParams();
    Object.keys(params || {}).forEach(function (key) {
        var value = params[key];
        if (value == null || value === '') {
            return;
        }
        if (Array.isArray(value)) {
            value.forEach(function (item) {
                if (item != null && item !== '') {
                    search.append(key + '[]', item);
                }
            });
            return;
        }
        search.set(key, value);
    });
    return search.toString();
}

var LISTING_AGG_COLOR = 'rgba(37,99,235,0.92)';

function buildAggHtml(count, saleCount, rentCount) {
    if (saleCount > 0 && rentCount > 0) {
        return '<div class="nm-combined-badge nm-combined-badge--listing">'
            + '<span class="nm-combined-badge-listing-sale">' + saleCount + '</span>'
            + '<span class="nm-combined-badge-sep"></span>'
            + '<span class="nm-combined-badge-listing-rent">' + rentCount + '</span>'
            + '</div>';
    }
    var size = count < 10 ? 11 : count < 100 ? 14 : count < 1000 ? 17 : 20;
    var diameter = size * 2;
    return '<div class="nm-agg-dot nm-agg-dot--listing" style="width:' + diameter
        + 'px;height:' + diameter + 'px;background:' + LISTING_AGG_COLOR + '">'
        + '<span class="nm-agg-count">' + count + '</span></div>';
}

function formatPriceCompact(value, offerType) {
    var suffix = offerType === 'rent' ? ' zł/mc' : ' zł';
    if (value == null || value === '') {
        return 'Do uzgodnienia';
    }
    var num = Number(value);
    if (!isFinite(num)) {
        return 'Do uzgodnienia';
    }
    if (num >= 1000000) {
        return num / 1000000 % 1 === 0
            ? (num / 1000000) + ' mln' + suffix
            : (num / 1000000).toLocaleString('pl-PL', { maximumFractionDigits: 2 }) + ' mln' + suffix;
    }
    if (num >= 100000) {
        return Math.round(num / 1000).toLocaleString('pl-PL') + ' tys.' + suffix;
    }
    return num.toLocaleString('pl-PL', { maximumFractionDigits: 0 }) + suffix;
}

// The marker keeps its short price ("400 tys. zł"); the full price is its
// accessible name and tooltip.
function formatPriceFull(value, offerType) {
    var num = Number(value);
    if (value == null || value === '' || !isFinite(num)) {
        return 'Cena ofertowa do uzgodnienia';
    }
    return 'Cena ofertowa ' + Math.round(num).toLocaleString('pl-PL', { useGrouping: 'always' }) + ' zł'
        + (offerType === 'rent' ? ' miesięcznie' : '');
}

function buildMarkerHtml(props) {
    var offerClass = props.offer_type === 'rent' ? ' nm-listing-marker--rent' : ' nm-listing-marker--sale';
    var id = props.id == null ? '' : String(props.id);
    var fullPrice = formatPriceFull(props.price, props.offer_type);
    return '<div class="nm-listing-marker' + offerClass + '" data-listing-marker-id="' + escapeHtml(id) + '"'
        + ' role="img" aria-label="' + escapeHtml(fullPrice) + '" title="' + escapeHtml(fullPrice) + '">'
        + (props.has_photos ? '<span class="nm-listing-marker__photo" aria-label="Ma zdjęcia"></span>' : '')
        + '<span class="nm-listing-marker__price">' + escapeHtml(formatPriceCompact(props.price, props.offer_type)) + '</span>'
        + '</div>';
}

export default class ListingsLayer extends GeoJsonFetchLayer {
    static ID = 'ogloszenia';
    static DESCRIPTION = 'Ogłoszenia nieruchomości';
    static MIN_ZOOM = 0;
    static SOURCE_ID = 'src-property-listings';
    static BASE_LAYER_IDS = [];
    static RADIUS_SOURCE_ID = 'src-property-listings-radius';
    static RADIUS_LAYER_IDS = ['lyr-property-listings-radius-fill', 'lyr-property-listings-radius-line'];
    static PARCEL_SOURCE_ID = 'src-property-listings-parcels';
    static PARCEL_LAYER_IDS = ['lyr-property-listings-parcels-fill', 'lyr-property-listings-parcels-line'];
    static RENDER_ORDER_LAYER_IDS = [];

    getSourceId() {
        return ListingsLayer.SOURCE_ID;
    }

    getLayerIds() {
        return ListingsLayer.BASE_LAYER_IDS;
    }

    getRenderOrderLayerIds() {
        return ListingsLayer.RENDER_ORDER_LAYER_IDS.concat(ListingsLayer.PARCEL_LAYER_IDS, ListingsLayer.RADIUS_LAYER_IDS);
    }

    buildUrl() {
        var params = Object.assign(
            {
                zoom: this.config.getCurrentZoom(),
                limit: 800,
            },
            bboxParamsFromString(this.config.getCurrentBbox ? this.config.getCurrentBbox(0.35) : ''),
            this.config.getListingFilterParams ? this.config.getListingFilterParams() : {}
        );

        return this.config.baseUrl + 'api/listings/geojson?' + queryString(params);
    }

    renderAggregatedData(data) {
        var map = this.getMap();
        if (!map) {
            return null;
        }

        var self = this;
        var group = new MarkerGroup();
        groupLayerMarkers(data.features, map, data.aggregated === true).forEach(function (feature) {
            if (!feature || !feature.geometry || feature.geometry.type !== 'Point') {
                return;
            }
            if (feature.properties?.aggregated !== true) {
                const marker = self.createFeatureMarker(feature);
                if (marker) group.add(marker);
                return;
            }
            var props = feature.properties || {};
            var coords = feature.geometry.coordinates;
            var count = Number(props.count || 0);
            if (!count) {
                return;
            }
            group.add(createHtmlMarker(
                coords,
                buildAggHtml(count, Number(props.sale_count || 0), Number(props.rent_count || 0)),
                'nm-listing-agg-wrap',
                {
                    onClick: function () {
                        drillDownTransactionCluster(map, feature);
                        if (self.config.onClusterClick) {
                            self.config.onClusterClick(props, { lat: coords[1], lng: coords[0] });
                        }
                    },
                }
            ));
        });

        group.addTo(map);
        return group;
    }

    createFeatureMarker(feature) {
        if (!feature || !feature.geometry || feature.geometry.type !== 'Point') {
            return null;
        }
        var self = this;
        var props = feature.properties || {};
        var coords = feature.geometry.coordinates;
        return createHtmlMarker(
            coords,
            buildMarkerHtml(props),
            'nm-listing-marker-wrap',
            {
                onClick: function () {
                    if (self.config.onSelectListing) {
                        self.config.onSelectListing(props, { lat: coords[1], lng: coords[0] });
                    }
                },
            }
        );
    }

    renderIndividualData(data) {
        var map = this.getMap();
        if (!map) {
            return null;
        }

        var self = this;
        this.renderParcelFeatures(data.parcel_features || []);
        var group = new MarkerGroup();
        (data.features || []).forEach(function (feature) {
            var marker = self.createFeatureMarker(feature);
            if (marker) {
                group.add(marker);
            }
        });

        group.addTo(map);
        return group;
    }

    renderParcelFeatures(features) {
        var map = this.getMap();
        if (!map) return;
        addOrUpdateGeoJsonSource(map, ListingsLayer.PARCEL_SOURCE_ID, {
            type: 'FeatureCollection',
            features: features || [],
        });
        if (!map.getLayer(ListingsLayer.PARCEL_LAYER_IDS[0])) {
            map.addLayer({
                id: ListingsLayer.PARCEL_LAYER_IDS[0],
                type: 'fill',
                source: ListingsLayer.PARCEL_SOURCE_ID,
                filter: ['==', ['get', 'role'], 'offered'],
                paint: { 'fill-color': '#2563eb', 'fill-opacity': 0.22 },
            });
        }
        if (!map.getLayer(ListingsLayer.PARCEL_LAYER_IDS[1])) {
            map.addLayer({
                id: ListingsLayer.PARCEL_LAYER_IDS[1],
                type: 'line',
                source: ListingsLayer.PARCEL_SOURCE_ID,
                paint: {
                    'line-color': '#1d4ed8',
                    'line-width': 2.5,
                    'line-dasharray': ['case', ['==', ['get', 'role'], 'access_road'], ['literal', [3, 2]], ['literal', [1, 0]]],
                },
            });
        }
        if (!this.parcelClickHandler) {
            var self = this;
            this.parcelClickHandler = function (event) {
                var feature = event.features && event.features[0];
                if (!feature || !self.config.onSelectListing) return;
                var props = feature.properties || {};
                props.id = Number(props.listing_id);
                self.config.onSelectListing(props, event.lngLat ? { lat: event.lngLat.lat, lng: event.lngLat.lng } : null);
            };
            ListingsLayer.PARCEL_LAYER_IDS.forEach(function (layerId) {
                map.on('click', layerId, self.parcelClickHandler);
            });
        }
    }

    getFilterFingerprint() {
        var params = this.config.getListingFilterParams ? this.config.getListingFilterParams() : {};
        return JSON.stringify(Object.assign({}, params || {}, {
            listing_radius_overlay: this.getRadiusFingerprint(),
        }));
    }

    getRadiusFilterState() {
        return this.config.getListingRadiusFilterState ? this.config.getListingRadiusFilterState() : null;
    }

    getRadiusFingerprint() {
        var state = this.getRadiusFilterState();
        if (!state) {
            return '';
        }
        return [
            state.lat || '',
            state.lng || '',
            state.radiusKm || '',
        ].join('|');
    }

    clearRadiusOverlay() {
        var map = this.getMap();
        if (!map) {
            return;
        }

        removeSourceAndLayers(map, ListingsLayer.RADIUS_SOURCE_ID, ListingsLayer.RADIUS_LAYER_IDS);
    }

    syncRadiusOverlay() {
        var map = this.getMap();
        var state = this.getRadiusFilterState();
        if (!map || !state) {
            this.clearRadiusOverlay();
            return;
        }

        var lat = Number(state.lat);
        var lng = Number(state.lng);
        var radiusKm = Number(state.radiusKm || 0);
        if (!isFinite(lat) || !isFinite(lng) || !isFinite(radiusKm) || radiusKm <= 0) {
            this.clearRadiusOverlay();
            return;
        }

        addOrUpdateGeoJsonSource(
            map,
            ListingsLayer.RADIUS_SOURCE_ID,
            buildRadiusPolygon(lng, lat, radiusKm * 1000, 96)
        );

        if (!map.getLayer(ListingsLayer.RADIUS_LAYER_IDS[0])) {
            map.addLayer({
                id: ListingsLayer.RADIUS_LAYER_IDS[0],
                type: 'fill',
                source: ListingsLayer.RADIUS_SOURCE_ID,
                paint: {
                    'fill-color': 'rgba(37,99,235,0.14)',
                    'fill-opacity': 1,
                },
            });
        }
        if (!map.getLayer(ListingsLayer.RADIUS_LAYER_IDS[1])) {
            map.addLayer({
                id: ListingsLayer.RADIUS_LAYER_IDS[1],
                type: 'line',
                source: ListingsLayer.RADIUS_SOURCE_ID,
                paint: {
                    'line-color': 'rgba(37,99,235,0.92)',
                    'line-width': 2.5,
                    'line-opacity': 0.95,
                },
            });
        }
    }

    renderData(data) {
        this.syncRadiusOverlay();
        return this.renderCapacityMarkerData(data);
    }

    clear() {
        this.abortPending();
        this.resetAccumulator();
        this.disposeKeyedMarkerGroup();

        var map = this.getMap();
        var layer = this.config.getLayer ? this.config.getLayer() : null;
        if (layer && typeof layer.remove === 'function') {
            layer.remove();
        }
        if (map) {
            removeSourceAndLayers(map, this.getSourceId(), this.getLayerIds());
            if (this.parcelClickHandler && typeof map.off === 'function') {
                ListingsLayer.PARCEL_LAYER_IDS.forEach((layerId) => map.off('click', layerId, this.parcelClickHandler));
            }
            this.parcelClickHandler = null;
            removeSourceAndLayers(map, ListingsLayer.PARCEL_SOURCE_ID, ListingsLayer.PARCEL_LAYER_IDS);
        }
        this.clearRadiusOverlay();
        if (this.config.setLayer) {
            this.config.setLayer(null);
        }
        if (this.config.onCleared) {
            this.config.onCleared(this);
        }
    }
}
